import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import Image from "next/legacy/image";
import { Text, AspectRatio, Button } from "@chakra-ui/react";
import {
  getPage,
  type Page as SanityPage,
  getAnnouncementBanner,
  type Banner,
  getStackedLogos,
  type Customer,
} from "lib/sanity";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { CenteredQuote } from "components/quotes";
import { Link } from "ui/link";
import { HalfHero } from "components/half-hero";
import { getPageMetaImage } from "utils";
import { PairImage } from "components/pair-image";
import { StackedLogos } from "components/logos";
import { AuraPlate } from "ui/plates";
import { integrationsCount } from "../../constants/integrations";
import heroImage from "../../../public/assets/solutions/databricks/hero.svg";
import fullWidthImage from "../../../public/assets/solutions/databricks/full-width.png";
import firstPairImage from "../../../public/assets/solutions/databricks/pair-1.svg";
import secondPairImage from "../../../public/assets/solutions/databricks/pair-2.png";
import thirdPairImage from "../../../public/assets/solutions/databricks/pair-3.png";
import fourthPairImage from "../../../public/assets/solutions/databricks/pair-4.svg";

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
  customerRows: Customer[][];
};

const Databricks: NextPage<Readonly<PageProps>> = ({ page, announcementBanner, customerRows }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHero
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        image={heroImage}
        imageRatio={3 / 2}
      />

      <StackedLogos title="" customerRows={customerRows} />

      <Section center textAlign="center" maxW="4xl" mx="auto">
        <Text as="h2" textStyle="h4">
          Get modeled data from your lakehouse into {integrationsCount}+ apps. Fast.
        </Text>

        <AspectRatio ratio={2 / 1} pos="relative" w="100%" mt={{ base: 8, md: 16 }}>
          <Image
            src={fullWidthImage}
            alt="Data moving through a datawarehouse and Hightouch into various marketing tools and campaigns."
            objectFit="contain"
            layout="fill"
          />
        </AspectRatio>

        <Button as={Link} mt={20} size="lg" href="/resources/reverse-etl-data-sheet">
          Read the data sheet
        </Button>
      </Section>

      <Section>
        <PairImage
          align="left"
          image={firstPairImage}
          title="Use your ML models for more than just analytics"
          actionText="Learn more"
          actionUrl="/blog/our-integration-with-databricks"
          description={
            <Text>
              Your lakehouse stores massive amounts of user and event data, which is transformed into ML models that
              power high-impact use cases like customer propensity to convert or churn. The next step? Sync those models
              into {integrationsCount}+ downstream tools so data and business teams can deliver stellar customer
              experiences, together.
            </Text>
          }
        />
      </Section>

      <Section>
        <PairImage
          align="right"
          image={secondPairImage}
          title="Transform your lakehouse into a powerful CDP"
          actionText="Learn more"
          actionUrl="/resources/composable-cdp-ebook-dbsp"
          description={
            <Text>
              From clickstream to unstructured data, your lakehouse has the first-party data you need to deliver
              personalized customer experiences. Train and productionize the models that matter to your business, and
              then activate them into any downstream tool with Hightouch.
            </Text>
          }
        />
      </Section>

      <AuraPlate condensed variant="green" py={0}>
        <CenteredQuote
          author={{
            name: "Ernie Prabhakar",
            job: "IT Business Apps Manager",
          }}
          company={{
            name: "Nauto",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/df9318f1f25cc6d94fe4f1cd8feba2155c3a1056-864x200.svg",
          }}
          quoteText="Hightouch makes it trivial to pull data out of Databricks directly into business systems that business users are familiar with, like Google Sheets for our Operations teams and Salesforce for our Support teams. With Hightouch, we use our single source of truth of our organization's data to drive mission-critical workflows."
        />
      </AuraPlate>

      <Section>
        <PairImage
          align="left"
          image={thirdPairImage}
          title="The lakehouse is for everyone"
          actionText="Learn more"
          actionUrl="/platform/customer-studio"
          description={
            <Text>
              Hightouch Customer Studio, an entire suite of no-code features, enables anyone within your organization to
              activate data from the lakehouse. Build audiences, run experiments and analyze performance with ease.
            </Text>
          }
        />
      </Section>

      <Section my={0} pb={sectionSpacingY}>
        <PairImage
          align="right"
          image={fourthPairImage}
          imageRatio={2 / 1}
          title="Get started, fast"
          actionText="Learn more"
          actionUrl="/blog/hightouch-databricks-partner-connect"
          description={
            <Text>
              Find Hightouch in Databricks Partner Connect, and activate your lakehouse in minutes. Write a SQL model or
              define an audience and start syncing data to any team, anywhere.
            </Text>
          }
        />
      </Section>
    </Page>
  );
};

export default Databricks;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner, customerRows] = await Promise.all([
    getPage("/solutions/databricks"),
    getAnnouncementBanner(),
    getStackedLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      customerRows,
    },
    revalidate: 60,
  };
};
